// Navigation.tsx
import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ChatModeSelection from "../pages/ChatModeSelection";
import Login from "../pages/auth/Login";
import Feedback from "../pages/Feedback";
import NotFound from "../pages/NotFound";
import Loader from "../components/Loader";
import { tokenCheck } from "../api/request";

const PersonaSelection = lazy(() => import("../pages/PersonaSelection"));
const ChatScreen = lazy(() => import("../pages/ChatScreen"));

const Navigation = () => {
  return (
    <Router basename="/">
      <MainContent />
    </Router>
  );
};

const MainContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [endSummary, setEndSummary] = useState<boolean>(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Helper flags
  const isLogin = location.pathname === "/";
  const isFeedback = location.pathname === "/feedback";
  const isProtectedRoute = !isLogin && !isFeedback;

  // Route guard: only protect non-login/non-feedback routes, and use token (not normalLogin)
  useEffect(() => {
    const hasToken = !!localStorage.getItem("token");

    if (isProtectedRoute && !hasToken) {
      navigate("/", { replace: true });
    }

    // Optional convenience: if user hits "/" but already has a token, send them in
    if (isLogin && hasToken) {
      navigate("/chat-mode", { replace: true });
    }
  }, [isProtectedRoute, isLogin, navigate, location.pathname]);

  // Start/stop background token checks only on protected routes with a token
  useEffect(() => {
    const hasToken = !!localStorage.getItem("token");

    // clear any previous interval when path changes
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Only poll when on protected routes + we have a token
    if (isProtectedRoute && hasToken) {
      const tick = () => {
        if (document.visibilityState === "visible") {
          tokenCheck(); // should no-op or logout gracefully on 401/403
        }
      };

      // Run once immediately (optional)
      tick();

      // Your original cadence (20s)
      intervalRef.current = setInterval(tick, 20_000);
    }

    const onVis = () => {
      const stillHasToken = !!localStorage.getItem("token");
      if (
        document.visibilityState === "visible" &&
        isProtectedRoute &&
        stillHasToken
      ) {
        tokenCheck();
      }
    };

    document.addEventListener("visibilitychange", onVis);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [isProtectedRoute, location.pathname]);

  return (
    <>
      {/* Hide header on login & feedback to match your original */}
      {location.pathname !== "/" && location.pathname !== "/feedback" && (
        <Header />
      )}

      <main className="align-content-center">
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/chat-mode" element={<ChatModeSelection />} />
          <Route
            path="/persona-selection"
            element={
              <Suspense
                fallback={
                  <div className="lazyLoader text-center">
                    <Loader />
                  </div>
                }
              >
                <PersonaSelection />
              </Suspense>
            }
          />
          <Route
            path="/chat-screen"
            element={
              <Suspense
                fallback={
                  <div className="lazyLoader text-center">
                    <Loader />
                  </div>
                }
              >
                <ChatScreen endSummaryButton={endSummary} />
              </Suspense>
            }
          />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      {location.pathname !== "/feedback" && <Footer />}
    </>
  );
};

export default Navigation;
